import React, {FC, useMemo, useRef} from 'react';
import s from './BaseAddToCartButton.scss';
import {ButtonContentType, IProduct} from '../../../types/galleryTypes';
import classNames from 'classnames';
import {
  HoverStyles,
  ButtonPriority,
  StatesButton,
  StatesButtonStates,
  ThreeDotsLoader,
  PaddingModes,
  Tooltip,
  TooltipProps,
} from '@wix/wix-ui-tpa/cssVars';
import {classes as addToCartStyle, st as addToCartStyles} from './BaseAddToCartButton.st.css';
import {useGlobals, withGlobals} from '../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../gallery/galleryGlobalStrategy';
import {useBaseAddToCartState} from './hooks/useBaseAddToCartState';
import {useBaseAddToCartIcon} from './hooks/useBaseAddToCartIcon';
import {IconStatesButton} from './IconStatesButton/IconStatesButton';
import {useBaseAddToCartNotificationEnd} from './hooks/useBaseAddToCartNotificationEnd';

export enum DataHook {
  BaseAddToCartLoadingIndicator = 'base-add-to-cart-loading-indicator',
  BaseAddToCartIconButtonTooltip = 'base-add-to-cart-icon-button-tooltip',
}

export interface IBaseAddToCartButton extends IGalleryGlobalProps {
  product: IProduct;
  contentType: ButtonContentType;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  disabled: boolean;
  text: string;
  className?: string;
  dataHook?: string;
  hasError?: boolean;
  errorMessage?: string;
  showOnHoverClassName?: string;
  isQuickAdd?: boolean;
  cardIndex?: number;
}

export const BaseAddToCartButtonComp: FC<IBaseAddToCartButton> = ({
  product: {id: productId},
  contentType,
  onClick,
  disabled,
  text,
  className = '',
  dataHook,
  hasError = false,
  errorMessage,
  showOnHoverClassName = '',
  isQuickAdd = false,
  cardIndex,
}) => {
  const {styles, stylesParams, shouldShowMobile} = useGlobals();

  const addToCartButtonRef = useRef<StatesButton>(null);
  const addToCartButtonState = useBaseAddToCartState(productId, cardIndex);
  const {handleNotificationEnd} = useBaseAddToCartNotificationEnd(productId);
  const {iconsMapper} = useBaseAddToCartIcon();

  const tooltipProps: TooltipProps = useMemo(
    () => ({
      'data-hook': DataHook.BaseAddToCartIconButtonTooltip,
      content: errorMessage,
      className: s.addToCartIconTooltip,
      disabled: !hasError,
      fixed: true,
      fluid: true,
      ...(shouldShowMobile
        ? {
            shown: hasError,
            placement: 'left',
          }
        : {}),
    }),
    [errorMessage, hasError, shouldShowMobile]
  );

  const shouldShowOnHover = styles.get(stylesParams.gallery_addToCartButtonShowOnHover);

  const addToCartButtonProps = {
    onClick,
    disabled,
    className: classNames(
      s.addToCartBtn,
      addToCartStyle.addToCartColorContrastFix,
      addToCartStyles(addToCartStyle.addToCartButton, {
        useMobileFont: shouldShowMobile,
        isInStock: !disabled,
        isDisabled: disabled,
      }),
      {
        [showOnHoverClassName]: shouldShowOnHover && !shouldShowMobile,
        [s.iconButton]: contentType === ButtonContentType.Icon,
      }
    ),
    'data-hook': dataHook,
    'aria-label': text,
    upgrade: contentType === ButtonContentType.IconAndText,
    fullWidth: true,
    hoverStyle: HoverStyles.underline,
    priority: ButtonPriority.primary,
    paddingMode: PaddingModes.explicitPaddings,
  };

  const getIconCompForIconAndTextButton = () => {
    const icon = styles.get(stylesParams.gallery_addToCartButtonIcon);

    const Icon = iconsMapper[icon];
    return <Icon className={s.prefixIcon} />;
  };

  //For Update Sleds
  const stateButtonProps = {
    state: addToCartButtonState,
    prefixIcon:
      addToCartButtonState === StatesButtonStates.IDLE && contentType === ButtonContentType.IconAndText
        ? getIconCompForIconAndTextButton()
        : undefined,
    idleContent: text,
    onNotificationEnd: handleNotificationEnd,
    ref: addToCartButtonRef,
  };

  return (
    <div
      className={classNames(s.addToCartBtnContainer, className, {
        [s.addToCartBtnContainerQuickAdd]: isQuickAdd,
      })}>
      {contentType === ButtonContentType.Icon ? (
        <Tooltip {...tooltipProps}>
          <IconStatesButton
            addToCartButtonProps={addToCartButtonProps}
            productId={productId}
            state={addToCartButtonState}
          />
        </Tooltip>
      ) : (
        <StatesButton
          {...addToCartButtonProps}
          {...stateButtonProps}
          wrapContent={true}
          inProgressContent={
            <div className={addToCartStyle.threeDotsButtonWrapper}>
              <span className={s.dummyTextForLoadingWidth}>{text}</span>
              <ThreeDotsLoader
                className={addToCartStyle.threeDotsButton}
                data-hook={DataHook.BaseAddToCartLoadingIndicator}
              />
            </div>
          }
        />
      )}
    </div>
  );
};

export const BaseAddToCartButton = withGlobals(BaseAddToCartButtonComp);
